<script setup>
import Column from 'primevue/column';
import { useSlots } from 'vue';
import { useDataTableStore } from '@stores/useDataTableStore';
import { trans } from '../../../_00_elements/translation';
import { useAccountStore } from '../store/account';
import InvoiceMenu from './menu/InvoiceMenu.vue';
import UbiqTable from '../../../_01_atoms/components/UbiqDataTable.vue';
import { PAY_CLIENT_BILLING_DOCUMENTS_ENDPOINT } from '../../../_00_elements/tools/routes/routes';
import { currency } from '../../../_00_elements/number-formatter';
import Tag from '../../../_01_atoms/components/Tag.vue';
import {
  getCompleteShorterReadableDate,
  getMonthDateFromIsoDate,
} from '../../../_00_elements/tools/date';
import CardEmptyTable from '../general/CardEmptyTable.vue';
import InvoiceTypeColumn from './datatable/InvoiceTypeColumn.vue';
import { BILLING_DOCUMENT_TYPES } from '../constants/constants';
import CreditNoteMenu from './menu/CreditNoteMenu.vue';

defineProps({
  hiddenColumns: {
    type: Array,
    default: () => [],
  },
});

const accountStore = useAccountStore();
const datatableStore = useDataTableStore();

if (!datatableStore.endpoint) {
  datatableStore.setEndpoint(PAY_CLIENT_BILLING_DOCUMENTS_ENDPOINT);
}

datatableStore.setDefaultOrder('billingDocument.createdAt', 'desc');

accountStore.setStripeAccountStatus(window.initialState.stripeAccountStatus);
const slots = useSlots();
const hasSlot = (slot) => !!slots[slot];
</script>

<template>
  <div>
    <UbiqTable data-key="row_id" scrollable>
      <Column
        v-if="!hiddenColumns.includes('amount')"
        field="billingDocument.amount"
        :header="trans('gestion.invoices.table.amount_ht')"
        sortable
        class="column--currency column--bold"
      >
        <template #body="{ data }">
          {{
            data.type === 'invoice'
              ? currency(data.amount / 100)
              : currency(data.amount * -1 / 100)
          }}
        </template>
      </Column>

      <Column v-if="!hiddenColumns.includes('status')">
        <template #body="{ data }">
          <div class="status-label" :class="[ `status-label--${data.status?.toLowerCase()}` ]">
            {{ trans(`gestion.invoice.status.${data.type}.${data.status?.toLowerCase()}`) }}
          </div>
        </template>
      </Column>

      <Column
        v-if="!hiddenColumns.includes('type')"
        field="billingDocument.type"
        :header="trans('gestion.invoices.table.type')"
        sortable
      >
        <template #body="{ data }">
          <InvoiceTypeColumn :type="data.type" :is-recurring="data.recurring" />
        </template>
      </Column>

      <Column
        v-if="!hiddenColumns.includes('client')"
        field="clientInvoiceData.email"
        :header="trans('gestion.invoices.table.client')"
        sortable
      >
        <template #body="{ data }">
          {{ data.subscription?.pay_organization_buyer?.client_invoice_data?.email }}
        </template>
      </Column>

      <Column
        v-if="!hiddenColumns.includes('seller')"
        field="clientInvoiceData.email"
        :header="trans('gestion.invoices.table.seller')"
        sortable
      >
        <template #body="{ data }">
          {{ data.subscription?.pay_organization_seller?.billing_invoice_data?.email }}
        </template>
      </Column>

      <Column
        v-if="!hiddenColumns.includes('issuedDate')"
        field="billingDocument.issuedDate"
        :header="trans('gestion.invoices.table.period')"
        sortable
      >
        <template #body="{ data }">
          <Tag
            v-if="data.issued_date"
            text-color="black"
            background-color="gray-light"
            class="invoice-date"
          >
            {{ getMonthDateFromIsoDate(data.issued_date.iso) }}
          </Tag>
          <span v-else>-</span>
        </template>
      </Column>

      <Column
        v-if="!hiddenColumns.includes('invoiceNumber')"
        field="billingDocument.invoiceNumber"
        sortable
        :header="trans('gestion.invoices.table.invoice_number')"
      >
        <template #body="{ data }">
          {{ data.invoice_number }}
        </template>
      </Column>

      <Column
        v-if="!hiddenColumns.includes('createdAt')"
        field="billingDocument.createdAt"
        :header="trans('gestion.invoices.table.created_date')"
        sortable
      >
        <template #body="{ data }">
          {{ getCompleteShorterReadableDate(data.created_at.iso) }}
        </template>
      </Column>

      <Column frozen align-frozen="right">
        <template #body="{ data }">
          <template v-if="hasSlot('actions')">
            <slot name="actions" :data="data" />
          </template>
          <div v-else class="simple-table__column--actions">
            <InvoiceMenu v-if="data.type === BILLING_DOCUMENT_TYPES.INVOICE" :invoice="data" />
            <CreditNoteMenu v-if="data.type === BILLING_DOCUMENT_TYPES.CREDIT_NOTE" :invoice="data" />
          </div>
        </template>
      </Column>

      <template #empty-state>
        <template v-if="hasSlot('empty-state')">
          <slot name="empty-state" />
        </template>
        <template v-else>
          <CardEmptyTable
            logo="ph_coins_blue"
            :title="trans('gestion.invoice.empty.title')"
            :subtitle="trans('gestion.invoice.empty.subtitle')"
          />
        </template>
      </template>
    </UbiqTable>
  </div>
</template>
